import React, { useState, useEffect, useCallback, useRef } from 'react';
import { logsApi } from '../../services/api';
import { Activity, User, FileText, Video, Tag, Clock, Filter, RefreshCw, AlertCircle, ClipboardCheck } from 'lucide-react';

const PAGE_SIZE = 50;

const ACTION_BADGE = {
  create:       { label: 'Create',       classes: 'bg-green-100 text-green-700' },
  update:       { label: 'Update',       classes: 'bg-blue-100 text-blue-700' },
  delete:       { label: 'Delete',       classes: 'bg-red-100 text-red-700' },
  publish_toggle: { label: 'Publish',    classes: 'bg-amber-100 text-amber-700' },
  publish:      { label: 'Publish',       classes: 'bg-amber-100 text-amber-700' },
  unpublish:    { label: 'Unpublish',     classes: 'bg-amber-100 text-amber-700' },
  file_upload:  { label: 'Upload',       classes: 'bg-teal-100 text-teal-700' },
  file_delete:  { label: 'File Delete',  classes: 'bg-orange-100 text-orange-700' },
  role_change:  { label: 'Role Change',  classes: 'bg-purple-100 text-purple-700' },
  login:        { label: 'Login',        classes: 'bg-gray-100 text-gray-600' },
  logout:       { label: 'Logout',       classes: 'bg-gray-100 text-gray-500' },
};

const ENTITY_ICONS = {
  policy: FileText,
  video:  Video,
  user:   User,
  folder: Tag,
  tag:    Tag,
  acknowledgement: ClipboardCheck,
};

function getActionVariant(action) {
  const operation = String(action || '').split('.').slice(1).join('.');
  return ACTION_BADGE[operation] || { label: action, classes: 'bg-gray-100 text-gray-600' };
}

function ActionBadge({ action }) {
  const variant = getActionVariant(action);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${variant.classes}`}>
      {variant.label}
    </span>
  );
}

function RelativeTime({ timestamp }) {
  const date = new Date(timestamp);
  if (!Number.isFinite(date.getTime())) return <span className="text-sm text-gray-400">—</span>;
  const now = Date.now();
  const diff = Math.floor((now - date.getTime()) / 1000);

  let relative;
  if (diff < 60) relative = 'just now';
  else if (diff < 3600) relative = `${Math.floor(diff / 60)}m ago`;
  else if (diff < 86400) relative = `${Math.floor(diff / 3600)}h ago`;
  else relative = `${Math.floor(diff / 86400)}d ago`;

  return (
    <span title={date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} className="text-sm text-gray-500 cursor-default whitespace-nowrap">
      {relative}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[...Array(5)].map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const fetchId = useRef(0);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async (silent = false) => {
    const id = ++fetchId.current;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      setError('');
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (entityTypeFilter) params.entityType = entityTypeFilter;
      if (actionFilter) params.action = actionFilter;
      const data = await logsApi.getAll(params);
      if (id !== fetchId.current) return;
      if (!Array.isArray(data.logs) || !Number.isFinite(Number(data.total))) {
        throw new Error('Invalid activity log data from server');
      }
      setLogs(data.logs);
      setTotal(Number(data.total));
    } catch (err) {
      if (id === fetchId.current) setError(err.message || 'Failed to fetch logs');
    } finally {
      if (id === fetchId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [page, entityTypeFilter, actionFilter]);

  // Fetch on filter/page change
  useEffect(() => {
    fetchLogs(false);
    return () => { fetchId.current += 1; };
  }, [fetchLogs]);

  // Reset to page 0 when filters change
  const handleEntityTypeChange = (v) => { setEntityTypeFilter(v); setPage(0); };
  const handleActionChange = (v) => { setActionFilter(v); setPage(0); };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load activity logs</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button onClick={() => fetchLogs(false)} className="px-6 py-3 bg-[#00AAFF] text-white font-medium rounded-xl hover:bg-[#0088cc] transition-colors">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#00AAFF]" />
            Activity Logs
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Loading activity…' : refreshing ? 'Refreshing activity…' : `${total.toLocaleString()} ${total === 1 ? 'entry' : 'entries'} total`}
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Filter className="w-4 h-4" />
          </div>

          <select
            value={entityTypeFilter}
            onChange={e => handleEntityTypeChange(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]/30"
          >
            <option value="">All Types</option>
            <option value="policy">Policy</option>
            <option value="video">Video</option>
            <option value="user">User</option>
            <option value="folder">Folder</option>
            <option value="tag">Resource Tab</option>
            <option value="acknowledgement">Acknowledgement</option>
          </select>

          <select
            value={actionFilter}
            onChange={e => handleActionChange(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]/30"
          >
            <option value="">All Actions</option>
            <optgroup label="Policy">
              <option value="policy.create">policy.create</option>
              <option value="policy.update">policy.update</option>
              <option value="policy.delete">policy.delete</option>
              <option value="policy.publish">policy.publish</option>
              <option value="policy.unpublish">policy.unpublish</option>
              <option value="policy.file_upload">policy.file_upload</option>
            </optgroup>
            <optgroup label="Resource Tab">
              <option value="tag.create">Create resource tab</option>
              <option value="tag.delete">Delete resource tab</option>
            </optgroup>
            <optgroup label="Video">
              <option value="video.create">video.create</option>
              <option value="video.update">video.update</option>
              <option value="video.delete">video.delete</option>
              <option value="video.publish">video.publish</option>
              <option value="video.unpublish">video.unpublish</option>
              <option value="video.file_upload">video.file_upload</option>
            </optgroup>
            <optgroup label="User">
              <option value="user.login">user.login</option>
              <option value="user.logout">user.logout</option>
              <option value="user.role_change">user.role_change</option>
            </optgroup>
            <optgroup label="Acknowledgement">
              <option value="acknowledgement.create">acknowledgement.create</option>
            </optgroup>
          </select>

          <button
            onClick={() => fetchLogs(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Details</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(8)].map((_, i) => <SkeletonRow key={i} />)
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <Clock className="w-10 h-10 text-gray-300" />
                      <p className="font-medium text-gray-500">No activity yet</p>
                      <p className="text-sm">Admin actions will appear here once they occur.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map(log => {
                  const EntityIcon = ENTITY_ICONS[log.entity_type] || Activity;
                  return (
                    <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                      {/* User */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-[#00AAFF]/10 flex items-center justify-center flex-shrink-0">
                            <User className="w-3.5 h-3.5 text-[#00AAFF]" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-800 truncate max-w-[140px]">{log.user_name || '—'}</p>
                            <p className="text-xs text-gray-400 truncate max-w-[140px]">{log.user_email || ''}</p>
                          </div>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <ActionBadge action={log.action} />
                          <p className="text-xs text-gray-400 font-mono">{log.action.startsWith('tag.') ? `Resource Tab: ${log.action.slice(4)}` : log.action}</p>
                        </div>
                      </td>

                      {/* Entity */}
                      <td className="px-4 py-3">
                        {log.entity_type ? (
                          <div className="flex items-center gap-1.5">
                            <EntityIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-gray-700 truncate max-w-[120px]">{log.entity_name || '—'}</p>
                              <p className="text-xs text-gray-400 capitalize">{log.entity_type === 'tag' ? 'Resource Tab' : log.entity_type}</p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* Details */}
                      <td className="px-4 py-3 max-w-[200px]">
                        <p className="text-gray-600 truncate text-xs" title={log.details}>{log.details || '—'}</p>
                      </td>

                      {/* Time */}
                      <td className="px-4 py-3">
                        <RelativeTime timestamp={log.created_at} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
